import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ExternalLink } from 'lucide-react';
import { WeekView, MonthView, PALETTE, IN_REVIEW, iso, useCalendarRange, CalendarToolbar } from './calendarViews';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = { aktif: 'Onaylı', isg_inceleme: 'İSG İncelemesinde', onay_bekliyor: '2. Onay Bekliyor', talep_bilgisi_hatali: 'Talep Bilgisi Hatalı' };
const fmt = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : '-');

const EventDialog = ({ ev, colorOf, onClose }) => {
  const navigate = useNavigate();
  return (
    <Dialog open={!!ev} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="calendar-event-dialog">
        {ev && <>
          <DialogHeader><DialogTitle>Talep #{ev.request_number}</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <Badge variant="outline" className={IN_REVIEW.includes(ev.status) ? 'border-dashed text-slate-500' : 'border-emerald-300 text-emerald-700'}>{STATUS[ev.status] || ev.status}</Badge>
            <p><b>Lokasyon:</b> {ev.location_name}</p>
            <p><b>Firma:</b> {ev.company_name}{ev.on_behalf_of ? ` (${ev.on_behalf_of} adına)` : ''}</p>
            <p><b>Tarih:</b> {fmt(ev.start_date)} – {fmt(ev.end_date)}</p>
            <p><b>Saat:</b> {ev.start_time} – {ev.end_time}</p>
            <p><b>İş Tanımı:</b> {ev.job_description || '-'}</p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {ev.special_situations.map(s => <span key={s.id} className="rounded px-2 py-0.5 text-xs font-medium text-white" style={{ backgroundColor: colorOf({ special_situations: [s] }) }}>{s.title}</span>)}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => navigate(`/work-permits/${ev.id}/edit`)} data-testid="calendar-open-permit-btn"><ExternalLink className="h-4 w-4 mr-1" />Talebi Görüntüle</Button>
          </DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
  );
};

export const SpecialSituationsCalendar = ({ situations, locations }) => {
  const [view, setView] = useState('week');
  const [anchor, setAnchor] = useState(new Date());
  const [locationId, setLocationId] = useState('all');
  const [situationId, setSituationId] = useState('all');
  const [events, setEvents] = useState([]);
  const [picked, setPicked] = useState(null);

  const days = useCalendarRange(view, anchor);

  useEffect(() => {
    const params = { start: days[0], end: days[days.length - 1] };
    if (locationId !== 'all') params.location_id = locationId;
    if (situationId !== 'all') params.situation_id = situationId;
    axios.get(`${API}/special-situations/calendar`, { params }).then(r => setEvents(r.data)).catch(() => setEvents([]));
  }, [days, locationId, situationId]);

  const colorIdx = Object.fromEntries(situations.map((s, i) => [s.id, i]));
  const colorOf = (ev) => {
    const s = (situationId !== 'all' && ev.special_situations.find(x => x.id === situationId)) || ev.special_situations[0];
    return PALETTE[(colorIdx[s?.id] ?? situations.length) % PALETTE.length];
  };
  const props = { days, events, colorOf, onPick: setPicked, today: iso(new Date()) };

  return (
    <div className="space-y-4" data-testid="special-situations-calendar">
      <CalendarToolbar view={view} setView={setView} anchor={anchor} setAnchor={setAnchor} days={days}>
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger className="w-52" data-testid="calendar-location-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tüm lokasyonlar</SelectItem>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={situationId} onValueChange={setSituationId}>
            <SelectTrigger className="w-52" data-testid="calendar-situation-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tüm özel durumlar</SelectItem>{situations.map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}</SelectContent>
          </Select>
      </CalendarToolbar>
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600" data-testid="calendar-legend">
        {situations.map((s, i) => <span key={s.id} className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />{s.title}</span>)}
        <span className="flex items-center gap-1.5 ml-2"><span className="h-3 w-3 rounded-sm border border-dashed border-slate-500 bg-slate-100" />İncelemedeki talep (kesikli kenar)</span>
        <span className="ml-auto text-slate-400" data-testid="calendar-count">{events.length} talep</span>
      </div>
      {view === 'week' ? <WeekView {...props} /> : <MonthView {...props} month={anchor.getMonth()} />}
      <EventDialog ev={picked} colorOf={colorOf} onClose={() => setPicked(null)} />
    </div>
  );
};
